import { useState } from "react";
import { toast } from "sonner";
import { formatTimestamp } from "@brainlog/shared";
import { errorMessage } from "@/lib/api";
import { useMe } from "@/lib/queries";
import type { Tab } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getEditorBridge } from "./editorBridge";
import { useNoteComments } from "./useNoteComments";

export function CommentsPanel({ tab }: { tab: Tab | null }) {
  if (!tab || tab.kind !== "note" || !tab.id) return <p className="p-3 text-sm text-muted-foreground">Comments are available on notes. Open a note to see or add comments.</p>;
  return <NoteComments noteId={tab.id} />;
}

export function NoteComments({ noteId }: { noteId: string }) {
  const me = useMe();
  const comments_ = useNoteComments(noteId);
  const { note, scopeId, unlocked } = comments_;
  const comments = { data: comments_.loaded ? { comments: comments_.comments } : undefined };
  const [body, setBody] = useState("");
  const [anchor, setAnchor] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const list = (comments.data?.comments ?? []).filter((c) => showResolved || !c.resolvedAt);
  const resolvedCount = (comments.data?.comments ?? []).filter((c) => c.resolvedAt).length;
  const readOnly = !!note.data?.note.deletedAt;

  if (scopeId && !unlocked) return <p className="p-3 text-sm text-muted-foreground">This note is end-to-end encrypted. Unlock it to read and add comments.</p>;

  return (
    <div className="space-y-3 p-3">
      {scopeId && <p className="text-xs text-muted-foreground">Comments on this note are end-to-end encrypted like the note itself.</p>}
      <p className="text-xs text-muted-foreground">
        {(note.data?.workspace.memberCount ?? 1) > 1 ? `Comments are visible to all members of this ${note.data?.workspace.kind === "notebook" ? "notebook" : "project"}.` : "Comments here are visible only to you until you share it."}
      </p>
      {!readOnly && (
        <form
          className="space-y-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!body.trim()) return;
            try {
              await comments_.add(body.trim(), anchor);
              setBody("");
              setAnchor(null);
            } catch (err) {
              toast.error(errorMessage(err));
            }
          }}
        >
          <Label htmlFor="new-comment">New comment</Label>
          <Textarea id="new-comment" rows={3} value={body} onChange={(e) => setBody(e.target.value)} maxLength={20_000} />
          {anchor ? (
            <p className="text-xs">
              On: “{anchor.slice(0, 160)}
              {anchor.length > 160 ? "…" : ""}”{" "}
              <button type="button" className="underline" onClick={() => setAnchor(null)}>
                remove
              </button>
            </p>
          ) : (
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => {
                const sel = getEditorBridge(noteId)?.selection().text.trim();
                if (sel) setAnchor(sel.slice(0, 2000));
                else toast.info("Select text in the editor first.");
              }}
            >
              Attach the selected text
            </Button>
          )}
          <Button type="submit" size="sm" disabled={!body.trim()}>
            Add comment
          </Button>
        </form>
      )}
      <ul className="space-y-2">
        {list.map((c) => (
          <li key={c.id} className="rounded-md border bg-background p-2 text-sm">
            <p className="text-xs text-muted-foreground">
              {c.authorName} · {formatTimestamp(c.createdAt, me.data?.timezone)}
              {c.resolvedAt && " · resolved"}
            </p>
            {c.anchorQuote && <blockquote className="my-1 border-l-2 pl-2 text-xs italic">{c.anchorQuote}</blockquote>}
            <p className="whitespace-pre-wrap">{c.body}</p>
            {!readOnly && (
              <div className="mt-1 flex gap-1">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => void comments_.resolve(c.id, !c.resolvedAt)}
                >
                  {c.resolvedAt ? "Reopen" : "Resolve"}
                </Button>
                {c.authorId === me.data?.id && (
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => void comments_.remove(c.id)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
        {comments.data && !list.length && <li className="text-sm text-muted-foreground">No open comments.</li>}
      </ul>
      {resolvedCount > 0 && (
        <Button size="xs" variant="link" onClick={() => setShowResolved(!showResolved)}>
          {showResolved ? "Hide" : "Show"} {resolvedCount} resolved
        </Button>
      )}
    </div>
  );
}
